import axios from "axios";
import { API_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import { loadRazorpayScript, RAZORPAY_CONFIG } from "../config/razorpay.config";
import { Logger } from '../utils/logger';

export const API_ENDPOINT = API_URL + "api/v1";

// Types
export interface SubscriptionPlan {
  _id: string;
  name: string;
  planId: string;
  amount: number;
  monthlyAmount: number;
  currency: string;
  period: "yearly" | "quarterly";
  interval: string;
  planType: "premium" | "ultimate";
}


export interface RazorpayOrderResponse {
  id: string;
  currency: string;
  amount: number;
  apiKey: string;
  status?: string;
  created_at?: number;
}
export interface Subscription {
  _id: string;
  subscriptionPlan: SubscriptionPlan;
  status: string;
  quantity: number;
  isTrail: boolean;
  trailStartedAt?: string;
  extraDetails: {
    current_start: number | null;
    current_end: number | null;
    ended_at: number | null;
    created_at: number;
    id?: string;
    entity?: string;
    plan_id?: string;
    customer_id?: string;
    notes?: {
      systemUserId?: string;
      systemSubscriptionId?: string;
      phone?: string;
      email?: string;
    };
  };
  createdAt: string;
  customerId?: string;
  organization?: string;
  subscriptionId?: string;
  paymentGateway?: string;
  isDeleted?: boolean;
  updatedAt?: string;
}

export interface SubscriptionUsage {
  totalUsers: number;
  plan: string;
  planType: "premium" | "ultimate";
  daysRemaining: number;
  validUntil: string;
  isActive: boolean;
  isTrial: boolean;
  trialDaysLeft: number;
  monthlyPerUserCost: number;
  totalAmount: string;
  currency: string;
}

export interface CreatePaymentOrderPayload {
  plan: string; // Keep as 'plan' to match backend
  quantity: number;
}
export interface PaymentOrder extends RazorpayOrderResponse {}

// API Calls
export const getCurrentSubscription = () => {
  Logger.log('Fetching current subscription...');
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  
  return axios.get<{ data: Subscription }>(`${API_ENDPOINT}/subscription`, { 
    headers 
  }).then(response => {
    Logger.log('Subscription fetched successfully:', response.data);
    return response;
  }).catch(error => {
    console.error('Error fetching subscription:', error);
    throw error;
  });
};

export const createPaymentOrder = async (payload: CreatePaymentOrderPayload) => {
  Logger.log('Creating payment order...', payload);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  try {
    const response = await axios.post<{ statusCode: number; status: boolean; message: string; data: PaymentOrder }>(
      `${API_ENDPOINT}/subscription/razorpay-order`,
      payload,
      { headers }
    );

    // Response structure: { statusCode, status, message, data: { id, currency, amount, apiKey } }
    if (!response.data || !response.data.data || !response.data.data.id) {
      throw new Error('Invalid order response from server');
    }

    Logger.log('Payment order created successfully:', response.data.data);
    return response;
  } catch (error) {
    console.error('Error creating payment order:', error);
    throw error;
  }
};

export const cancelSubscription = async (subscriptionId: string) => {
  Logger.log('Cancelling subscription...', subscriptionId);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  return axios.post(`${API_ENDPOINT}/subscription/${subscriptionId}/cancel`, {
    isImmediately: true
  }, { headers })
    .then(response => {
      Logger.log('Subscription cancelled successfully:', response.data);
      return response;
    })
    .catch(error => {
      console.error('Error cancelling subscription:', error);
      throw error;
    });
};

export const startTrial = async (planId: string) => {
  Logger.log('Starting trial...', planId);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  return axios.post(`${API_ENDPOINT}/subscription/trial`, {
    isTrail: true,
    plan: planId
  }, { headers })
    .then(response => {
      Logger.log('Trial started successfully:', response.data);
      return response;
    })
    .catch(error => {
      console.error('Error starting trial:', error);
      throw error;
    });
};

// Date Calculations
export const calculateSubscriptionEndDate = (subscription: Subscription): Date => {
  Logger.log('Calculating subscription end date...');
  Logger.log('Creation date:', subscription.createdAt);
  Logger.log('Trail started at:', subscription.trailStartedAt); // ✅ Added trail start logging
  Logger.log('Plan period:', subscription.subscriptionPlan.period);
  Logger.log('Is trial:', subscription.isTrail);

  if (subscription.isTrail) {
    // ✅ Use trailStartedAt for trial calculations
    const trailStartDate = new Date(subscription.trailStartedAt || subscription.createdAt);
    const endDate = new Date(trailStartDate);
    
    // ✅ Trial period - 7 days (fixed duration)
    endDate.setDate(endDate.getDate() + 7);
    Logger.log('Trial end date calculated:', endDate);
    return endDate;
  } else {
    // ✅ Use createdAt for regular subscriptions
    const createdDate = new Date(subscription.createdAt);
    const endDate = new Date(createdDate);
    
    // Regular subscription periods
    if (subscription.subscriptionPlan.period === "yearly") {
      endDate.setFullYear(endDate.getFullYear() + 1);
    } else if (subscription.subscriptionPlan.period === "quarterly") {
      endDate.setMonth(endDate.getMonth() + 3);
    }
    Logger.log('Subscription end date calculated:', endDate);
    return endDate;
  }
};

export const calculateDaysRemaining = (subscription: Subscription): number => {
  Logger.log('Calculating days remaining...');

  const endDate = calculateSubscriptionEndDate(subscription);
  const now = new Date();
  
  const daysRemaining = Math.max(0, Math.ceil(
    (endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
  ));
  
  Logger.log('Days remaining:', daysRemaining);
  return daysRemaining;
};

// Formatting
export const formatValidityDate = (subscription: Subscription): string => {
  Logger.log('Formatting validity date...');
  
  const endDate = calculateSubscriptionEndDate(subscription);
  const formattedDate = endDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  Logger.log('Formatted date:', formattedDate);
  return formattedDate;
};

export const formatSubscriptionPrice = (subscription: Subscription): string => {
  Logger.log('Formatting subscription price...');
  
  const amount = subscription.subscriptionPlan.amount;
  const currency = subscription.subscriptionPlan.currency;
  
  // Use Indian formatting for INR
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
  
  Logger.log('Formatted price:', formatted);
  return formatted;
};

// Status and Usage
export const isSubscriptionActive = (subscription: Subscription): boolean => {
  Logger.log('Checking subscription status...');
  
  if (subscription.status !== 'active') {
    Logger.log('Subscription not active, status:', subscription.status);
    return false;
  }
  
  const daysRemaining = calculateDaysRemaining(subscription);
  const isActive = daysRemaining > 0;
  
  Logger.log('Is active:', isActive);
  return isActive;
};

export const getMonthlyPerUserCost = (subscription: Subscription): number => {
  Logger.log('Calculating monthly per user cost...');
  
  const monthlyAmount = subscription.subscriptionPlan.monthlyAmount;
  const perUserCost = monthlyAmount / subscription.quantity;
  
  Logger.log('Monthly per user cost:', perUserCost);
  return perUserCost;
};

export const getSubscriptionUsage = (subscription: Subscription): SubscriptionUsage => {
  Logger.log('Getting subscription usage details...');
  
  const usage: SubscriptionUsage = {
    totalUsers: subscription.quantity,
    plan: subscription.subscriptionPlan.name,
    planType: subscription.subscriptionPlan.planType,
    daysRemaining: calculateDaysRemaining(subscription),
    validUntil: formatValidityDate(subscription),
    isActive: isSubscriptionActive(subscription),
    isTrial: subscription.isTrail,
    trialDaysLeft: subscription.isTrail ? calculateDaysRemaining(subscription) : 0,
    monthlyPerUserCost: getMonthlyPerUserCost(subscription),
    totalAmount: formatSubscriptionPrice(subscription),
    currency: subscription.subscriptionPlan.currency
  };
  
  Logger.log('Subscription usage:', usage);
  return usage;
};

// Razorpay Integration
export const initializeRazorpayPayment = async (
  paymentOrder: RazorpayOrderResponse,
  userDetails: {
    email: string;
    phone?: string;
    name: string;
  },
  callbacks: {
    onSuccess: (response: any) => void;
    onError: (error: any) => void;
    onDismiss?: () => void;
  }
) => {
  try {
    await loadRazorpayScript();

    const options = {
      key: paymentOrder.apiKey,
      amount: paymentOrder.amount,
      currency: paymentOrder.currency,
      name: RAZORPAY_CONFIG.COMPANY_NAME,
      description: RAZORPAY_CONFIG.DESCRIPTION,
      image: RAZORPAY_CONFIG.COMPANY_LOGO,
      order_id: paymentOrder.id,
      prefill: {
        email: userDetails.email,
        contact: userDetails.phone,
        name: userDetails.name,
      },
      handler: function(response: any) {
        Logger.log('Payment successful:', response);
        callbacks.onSuccess(response);
      },
      modal: {
        ondismiss: function() {
          Logger.log('Checkout form closed');
          callbacks.onDismiss?.();
        }
      },
      theme: {
        color: RAZORPAY_CONFIG.THEME_COLOR
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

    rzp.on('payment.failed', function(response: any) {
      console.error('Payment failed:', response.error);
      callbacks.onError(response.error);
    });

  } catch (error) {
    console.error('Error initializing Razorpay:', error);
    callbacks.onError(error);
  }
};


// Test Helper
export const testSubscription = (subscription: Subscription) => {
  Logger.log('\n=== Testing Subscription ===');
  Logger.log('Plan:', subscription.subscriptionPlan.name);
  Logger.log('Type:', subscription.subscriptionPlan.planType);
  Logger.log('Period:', subscription.subscriptionPlan.period);
  Logger.log('Status:', subscription.status);
  Logger.log('Is Trial:', subscription.isTrail);
  Logger.log('Users:', subscription.quantity);
  
  const usage = getSubscriptionUsage(subscription);
  Logger.log('\nUsage Details:');
  Logger.log('Days Remaining:', usage.daysRemaining);
  Logger.log('Valid Until:', usage.validUntil);
  Logger.log('Is Active:', usage.isActive);
  Logger.log('Monthly/User:', usage.monthlyPerUserCost);
  Logger.log('Total Amount:', usage.totalAmount);
  
  if (usage.isTrial) {
    Logger.log('Trial Days Left:', usage.trialDaysLeft);
  }
  
  return usage;
};